import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, where, onSnapshot, doc, getDoc, getDocs, getCountFromServer, Timestamp } from 'firebase/firestore';
import { 
  ShieldCheck, 
  BarChart3, 
  BookOpen, 
  TrendingUp, 
  AlertTriangle,
  ChevronRight,
  Zap,
  Settings,
  Loader2,
  FileEdit // Import a new icon for the article link
} from 'lucide-react';

// Module-level cache so fraudMatrix is only fetched once per session
let cachedMatrixMap = null;

const getUserLevel = (postCount, karma) => {
  if (karma >= 100 && postCount >= 20) return "DAO Guardian";
  if (karma >= 50 && postCount >= 10) return "Lead Contributor";
  if (karma >= 25 && postCount >= 5) return "Senior Member";
  if (karma >= 10 || postCount >= 2) return "Active Member";
  return "New Contributor";
};

export default function Sidebar() {
  const { currentUser, initializing } = useAuth();
  const [postCount, setPostCount] = useState(0);
  const [karma, setKarma] = useState(0);
  const [verifiedCount, setVerifiedCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);

  const [trendingTopics, setTrendingTopics] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(true);

  useEffect(() => {
    // Wait for auth to initialize before querying Firestore
    if (initializing) return;

    if (!currentUser) {
      setPostCount(0);
      setKarma(0);
      setVerifiedCount(0);
      setIsAdmin(false);
      return;
    }

    const userPostsQuery = query(collection(db, 'posts'), where('authorId', '==', currentUser.uid));
    const verifiedPostsQuery = query(collection(db, 'posts'), where('authorId', '==', currentUser.uid), where('verified', '==', true));

    Promise.all([
      getDocs(userPostsQuery),
      getCountFromServer(verifiedPostsQuery),
      getDoc(doc(db, 'admins', currentUser.uid)),
    ]).then(([postsSnap, verifiedSnap, adminSnap]) => {
      let totalKarma = 0;
      postsSnap.forEach((d) => { totalKarma += (d.data().likes || []).length; });
      setPostCount(postsSnap.size);
      setKarma(totalKarma);
      setVerifiedCount(verifiedSnap.data().count);
      setIsAdmin(adminSnap.exists());
    }).catch(() => {});

    return () => {};
  }, [currentUser, initializing]);

  useEffect(() => {
    if (initializing) return;

    const calculateTrending = async () => {
      try {
      setLoadingTopics(true);
      const twoDaysAgo = Timestamp.fromMillis(Date.now() - 48 * 60 * 60 * 1000);
      const recentPostsQuery = query(
        collection(db, 'posts'),
        where('type', '==', 'CFE'),
        where('createdAt', '>=', twoDaysAgo)
      );

      const recentPostsSnapshot = await getDocs(recentPostsQuery);
      const topicCounts = {};
      recentPostsSnapshot.forEach(d => {
        const fraudId = d.data().fraudMatrixId;
        if (fraudId && fraudId !== 'other') {
          topicCounts[fraudId] = (topicCounts[fraudId] || 0) + 1;
        }
      });

      if (Object.keys(topicCounts).length === 0) {
        setTrendingTopics([]);
        setLoadingTopics(false);
        return;
      }

      // Use cached matrix map — only fetches from Firestore once per session
      if (!cachedMatrixMap) {
        const matrixSnapshot = await getDocs(collection(db, 'fraudMatrix'));
        cachedMatrixMap = {};
        matrixSnapshot.forEach(d => { cachedMatrixMap[d.id] = d.data().name; });
      }

      const sortedTopics = Object.entries(topicCounts)
        .map(([id, count]) => ({ id, name: cachedMatrixMap[id] || id, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 3);

      setTrendingTopics(sortedTopics);
      setLoadingTopics(false);
      } catch (e) {
        setLoadingTopics(false);
      }
    };

    calculateTrending();
  }, [initializing]);

  const userLevel = currentUser ? getUserLevel(postCount, karma) : '';

  return (
    <div className="space-y-6 sticky top-24">
      {currentUser && (
        <div className="glass-card rounded-xl p-5">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-400 to-purple-400 flex items-center justify-center text-white font-bold text-xl">
              {currentUser.displayName ? currentUser.displayName.substring(0, 2).toUpperCase() : 'U'}
            </div>
            <div>
              <h3 className="font-bold text-white">{currentUser.displayName || 'User'}</h3>
              <p className="text-sm text-gray-400">{userLevel}</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="glass-card rounded-lg p-2">
              <p className="text-lg font-bold text-cyan-400">{postCount}</p>
              <p className="text-xs text-gray-500">Reports</p>
            </div>
            <div className="glass-card rounded-lg p-2">
              <p className="text-lg font-bold text-purple-400">{karma}</p>
              <p className="text-xs text-gray-500">Karma</p>
            </div>
            <div className="glass-card rounded-lg p-2">
              <p className="text-lg font-bold text-green-400">{verifiedCount}</p>
              <p className="text-xs text-gray-500">Verified</p>
            </div>
          </div>
        </div>
      )}

      {/* Admin Panel Link */}
      {isAdmin && (
        <div className="glass-card rounded-xl p-5">
          <h3 className="font-bold text-lg text-white mb-4 flex items-center gap-2">
            <Settings size={20} className="text-green-400" />
            Admin Panel
          </h3>
          <div className="space-y-2">
            <Link to="/admin/matrix" className="w-full glass-card p-3 rounded-lg text-left hover:bg-white/5 transition-all group block">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-200">Manage Fraud Matrix</span>
                <ChevronRight size={16} className="text-gray-500 group-hover:text-white transition-colors" />
              </div>
            </Link>
            {/* --- THIS IS THE NEW LINK --- */}
            <Link to="/admin/learning" className="w-full glass-card p-3 rounded-lg text-left hover:bg-white/5 transition-all group block">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <FileEdit size={16} className="text-gray-300" />
                    <span className="font-medium text-gray-200">Manage Articles</span>
                </div>
                <ChevronRight size={16} className="text-gray-500 group-hover:text-white transition-colors" />
              </div>
            </Link>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="glass-card rounded-xl p-5">
        <h3 className="font-bold text-lg text-white mb-4 flex items-center gap-2">
          <Zap size={20} className="text-cyan-400" />
          Quick Actions
        </h3>
        <div className="space-y-2">
          <Link to="/submit" className="w-full glass-card p-3 rounded-lg text-left hover:bg-white/5 transition-all group block">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-yellow-500/20 to-orange-500/20 flex items-center justify-center">
                  <AlertTriangle size={16} className="text-yellow-400" />
                </div>
                <span className="font-medium text-gray-200">Report Fraud</span>
              </div>
              <ChevronRight size={16} className="text-gray-500 group-hover:text-white transition-colors" />
            </div>
          </Link>
          <Link to="/analytics" className="w-full glass-card p-3 rounded-lg text-left hover:bg-white/5 transition-all group block">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center">
                  <BarChart3 size={16} className="text-cyan-400" />
                </div>
                <span className="font-medium text-gray-200">Analytics</span>
              </div>
              <ChevronRight size={16} className="text-gray-500 group-hover:text-white transition-colors" />
            </div>
          </Link>
          <Link to="/learn" className="w-full glass-card p-3 rounded-lg text-left hover:bg-white/5 transition-all group block">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
                  <BookOpen size={16} className="text-purple-400" />
                </div>
                <span className="font-medium text-gray-200">Learn More</span>
              </div>
              <ChevronRight size={16} className="text-gray-500 group-hover:text-white transition-colors" />
            </div>
          </Link>
        </div>
      </div>

      {/* Trending Topics */}
      <div className="glass-card rounded-xl p-5">
        <h3 className="font-bold text-lg text-white mb-4 flex items-center gap-2">
          <TrendingUp size={20} className="text-cyan-400" />
          Trending Topics
        </h3>
        {loadingTopics ? (
            <div className="flex justify-center items-center h-24">
                <Loader2 className="animate-spin text-cyan-400" size={24} />
            </div>
        ) : trendingTopics.length > 0 ? (
            <div className="space-y-3">
                {trendingTopics.map((topic) => (
                    <div key={topic.id} className="flex items-center justify-between group cursor-pointer">
                        <div>
                        <p className="font-medium text-gray-200 group-hover:text-cyan-400 transition-colors">#{topic.name.replace(/\s+/g, '')}</p>
                        <p className="text-xs text-gray-500">{topic.count} reports in last 48h</p>
                        </div>
                        <div className="text-xs text-red-400 flex items-center gap-1">
                            <TrendingUp size={12} />
                        </div>
                    </div>
                ))}
            </div>
        ) : (
            <p className="text-sm text-gray-500 text-center py-4">No recent report data to show trends.</p>
        )}
      </div>

      {/* Mission Statement */}
      <div className="glass-card rounded-xl p-5">
        <h3 className="font-bold text-lg text-white mb-3 flex items-center gap-2">
          <ShieldCheck size={20} className="text-cyan-400" />
          Our Mission
        </h3>
        <p className="text-sm text-gray-400 leading-relaxed">
          To create a transparent, community-driven platform for tracking and exposing cryptocurrency fraud, 
          making the ecosystem safer for everyone.
        </p>
      </div>
    </div>
  );
}